<div
  class="result-container animate-in fade-in slide-in-from-bottom-4 duration-500"
>
  @if (isGrading) {
    <!-- Grading in progress -->
    <div class="text-center py-12">
      <div class="mb-6">
        <lucide-icon
          [img]="icons.Loader2"
          class="w-16 h-16 mx-auto animate-spin"
          [style.color]="themeColor"
        ></lucide-icon>
      </div>

      <h2 class="text-xl font-bold text-gray-900 mb-2">
        Grading Your Assignment
      </h2>
      <p class="text-gray-500 mb-8">{{ currentProgressLabel }}</p>

      <!-- Progress dots -->
      <div class="flex items-center justify-center gap-2">
        @for (step of progressSteps; track step.key; let i = $index) {
          <div
            class="w-3 h-3 rounded-full transition-all duration-300"
            [class.bg-green-500]="i <= gradingProgressStep"
            [class.bg-gray-200]="i > gradingProgressStep"
            [class.scale-125]="i === gradingProgressStep"
          ></div>
        }
      </div>
    </div>
  } @else if (result) {
    <!-- Confetti for good/excellent scores -->
    @if (showConfetti) {
      <app-confetti-canvas
        [particleCount]="confettiParticleCount"
        [colors]="confettiColors"
        [burstDelay]="200"
      ></app-confetti-canvas>
    }

    <!-- Result display -->
    <div class="text-center">
      <!-- Score Ring -->
      <div class="score-ring-wrapper mb-6">
        <svg viewBox="0 0 120 120" class="score-ring">
          <!-- Background circle -->
          <circle
            cx="60"
            cy="60"
            r="52"
            fill="none"
            stroke="#e5e7eb"
            stroke-width="8"
          />
          <!-- Progress circle -->
          <circle
            cx="60"
            cy="60"
            r="52"
            fill="none"
            [attr.stroke]="ringColor"
            stroke-width="8"
            stroke-linecap="round"
            class="score-ring-progress"
            [style.stroke-dasharray]="ringCircumference"
            [style.stroke-dashoffset]="ringOffset"
          />
        </svg>
        <!-- Percentage inside the ring -->
        <div class="score-ring-label">
          <span class="text-3xl font-bold text-gray-900">
            {{ gradePercentage }}%
          </span>
        </div>
      </div>

      <!-- Performance Level -->
      <div class="mb-1 flex justify-center">
        <span
          class="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold"
          [style.background-color]="ringColorLight"
          [style.color]="ringColor"
        >
          {{ performanceMessage }}
          <span>{{ performanceEmoji }}</span>
        </span>
      </div>

      <!-- Description -->
      <p class="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
        {{ performanceDescription }}
      </p>

      <!-- Stats Card -->
      <div
        class="stats-card rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden divide-y divide-gray-100"
      >
        <!-- Score -->
        <div class="flex items-center gap-3 px-4 py-3">
          <div
            class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            [style.background-color]="ringColorLight"
          >
            <lucide-icon
              [img]="icons.Trophy"
              class="w-[18px] h-[18px]"
              [style.color]="ringColor"
            ></lucide-icon>
          </div>
          <span class="text-sm text-gray-500">Score</span>
          <span class="ml-auto text-sm font-semibold text-gray-900">
            {{ pointsAwarded }} / {{ totalPoints }} points
          </span>
        </div>

        <!-- Grade -->
        <div class="flex items-center gap-3 px-4 py-3">
          <div
            class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            [style.background-color]="ringColorLight"
          >
            <lucide-icon
              [img]="icons.Target"
              class="w-[18px] h-[18px]"
              [style.color]="ringColor"
            ></lucide-icon>
          </div>
          <span class="text-sm text-gray-500">Grade</span>
          <span class="ml-auto text-sm font-semibold text-gray-900">
            {{ gradePercentage }}%
          </span>
        </div>

        <!-- Completed date -->
        @if (submittedAt) {
          <div class="flex items-center gap-3 px-4 py-3">
            <div
              class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              [style.background-color]="ringColorLight"
            >
              <lucide-icon
                [img]="icons.Calendar"
                class="w-[18px] h-[18px]"
                [style.color]="ringColor"
              ></lucide-icon>
            </div>
            <span class="text-sm text-gray-500">Completed</span>
            <span class="ml-auto text-sm font-semibold text-gray-900">
              {{ submittedAt | date : 'MMM d, yyyy, h:mm a' }}
            </span>
          </div>
        }

        <!-- Duration -->
        @if (duration) {
          <div class="flex items-center gap-3 px-4 py-3">
            <div
              class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              [style.background-color]="ringColorLight"
            >
              <lucide-icon
                [img]="icons.Clock"
                class="w-[18px] h-[18px]"
                [style.color]="ringColor"
              ></lucide-icon>
            </div>
            <span class="text-sm text-gray-500">Duration</span>
            <span class="ml-auto text-sm font-semibold text-gray-900">
              {{ duration }}
            </span>
          </div>
        }
      </div>
    </div>

    <!-- Fixed Footer -->
    <div class="fixed-footer">
      <div class="fixed-footer-inner">
        @if (isTestMode) {
          <!-- Test mode: Test Again (primary) + Back to Assignment (secondary) -->
          <button
            type="button"
            (click)="onRetest()"
            class="w-full py-4 px-6 rounded-xl font-semibold text-white text-lg flex items-center justify-center gap-2 transition-all duration-200 hover:opacity-90 active:scale-[0.98] shadow-lg cursor-pointer"
            [style.background-color]="themeColor"
          >
            <lucide-icon
              [img]="icons.RotateCcw"
              class="w-5 h-5"
            ></lucide-icon>
            <span>Test Again</span>
          </button>
          <button
            type="button"
            (click)="onContinue()"
            class="w-full py-3 px-6 rounded-xl font-semibold text-gray-700 text-sm bg-gray-100 hover:bg-gray-200 transition-all duration-200 active:scale-[0.98] cursor-pointer flex items-center justify-center gap-2 mt-3"
          >
            <span>Back to Assignment</span>
            <lucide-icon
              [img]="icons.ArrowRight"
              class="w-4 h-4"
            ></lucide-icon>
          </button>
        } @else {
          <!-- Student mode: Continue button -->
          <button
            type="button"
            (click)="onContinue()"
            class="w-full py-4 px-6 rounded-xl font-semibold text-white text-lg flex items-center justify-center gap-2 transition-all duration-200 hover:opacity-90 active:scale-[0.98] shadow-lg cursor-pointer"
            [style.background-color]="themeColor"
          >
            <span>Continue</span>
            <lucide-icon
              [img]="icons.ArrowRight"
              class="w-5 h-5"
            ></lucide-icon>
          </button>
        }
      </div>
    </div>
  }
</div>
